import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import GodReviewModal from '../components/GodReviewModal';
import ReviewSendTelegramModal from '../components/ReviewSendTelegramModal';
import { formatReviewSubject, formatReviewer, memberStatusIcon, formatViewObjectCompact } from '../utils/reviewLabels';
import { formatSingaporeDateTime, formatSingaporeDateDmy, isFutureSingaporeDateTime } from '../utils/singaporeTime';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiX, FiSearch, FiClock, FiCheckSquare } from 'react-icons/fi';
import { FaTelegramPlane } from 'react-icons/fa';

const GodReviewPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalReviews, setTotalReviews] = useState(0);
    const [selectedReview, setSelectedReview] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCancelScheduleConfirm, setShowCancelScheduleConfirm] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [forModelFilter, setForModelFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [ratingFilter, setRatingFilter] = useState('');
    const [hiddenFilter, setHiddenFilter] = useState('');
    const [lastPostFilter, setLastPostFilter] = useState('');
    const [recommendFilter, setRecommendFilter] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const getAuthHeaders = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true'
    }), []);

    const fetchReviews = useCallback(async (page, forModel = '', memberStatus = '', search = '', rating = '', hidden = '', lastPost = '', recommend = false) => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/godreviews?page=${page}&limit=20&sort=-publishDate`;
            if (forModel) apiUrl += `&reviewForModel=${forModel}`;
            if (memberStatus === 'active' || memberStatus === 'cancel') apiUrl += `&memberStatus=${memberStatus}`;
            if (search?.trim()) apiUrl += `&search=${encodeURIComponent(search.trim())}`;
            if (rating) apiUrl += `&rating=${rating}`;
            if (hidden) apiUrl += `&hidden=${encodeURIComponent(hidden)}`;
            if (lastPost === 'scheduled') apiUrl += '&lastPost=scheduled';
            if (recommend) apiUrl += '&recommend=true';
            const response = await axios.get(apiUrl, { headers: getAuthHeaders() });
            if (response.data.success) {
                setReviews(response.data.data);
                setTotalPages(response.data.totalPages ?? response.data.pagination?.pages ?? 1);
                setTotalReviews(response.data.total ?? response.data.pagination?.total ?? 0);
            }
        } catch (err) {
            setError(`Failed to fetch reviews: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchReviews(currentPage, forModelFilter, statusFilter, searchTerm, ratingFilter, hiddenFilter, lastPostFilter, recommendFilter);
    }, [fetchReviews, currentPage, forModelFilter, statusFilter, searchTerm, ratingFilter, hiddenFilter, lastPostFilter, recommendFilter]);

    useEffect(() => {
        if (searchParams.get('open') === 'create') {
            setShowCreateModal(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const refresh = () => fetchReviews(currentPage, forModelFilter, statusFilter, searchTerm, ratingFilter, hiddenFilter, lastPostFilter, recommendFilter);

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setForModelFilter('');
        setStatusFilter('');
        setRatingFilter('');
        setHiddenFilter('');
        setLastPostFilter('');
        setRecommendFilter(false);
        setCurrentPage(1);
    };

    const handleRecommend = () => {
        setSearchInput('');
        setSearchTerm('');
        setForModelFilter('godMember');
        setStatusFilter('active');
        setRatingFilter('');
        setHiddenFilter('');
        setLastPostFilter('');
        setRecommendFilter(true);
        setCurrentPage(1);
    };

    const openEdit = async (review) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godreviews/${review._id}`,
                { headers: getAuthHeaders() }
            );
            if (res.data.success) {
                setSelectedReview(res.data.data);
                setShowEditModal(true);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    const openView = async (review) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godreviews/${review._id}`,
                { headers: getAuthHeaders() }
            );
            if (res.data.success) {
                setSelectedReview(res.data.data);
                setShowViewModal(true);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    const openSend = (review) => {
        setSelectedReview(review);
        setShowSendModal(true);
    };

    const exitSelectMode = () => {
        setSelectMode(false);
        setSelectedIds([]);
        setShowBulkDeleteConfirm(false);
    };

    const toggleSelectId = (id) => {
        setSelectedIds((prev) => (
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        ));
    };

    const pageIds = reviews.map((review) => review._id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

    const toggleSelectPage = () => {
        if (allPageSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
            return;
        }
        setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/godreviews/${selectedReview._id}`,
                { headers: getAuthHeaders() }
            );
            setShowDeleteConfirm(false);
            setSelectedReview(null);
            refresh();
        } catch (err) {
            setError(`Failed to delete review: ${err.response?.data?.error || err.message}`);
        }
    };

    const confirmBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        try {
            setBulkDeleting(true);
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/godreviews/bulk-delete`,
                { ids: selectedIds },
                { headers: getAuthHeaders() }
            );
            setShowBulkDeleteConfirm(false);
            exitSelectMode();
            refresh();
        } catch (err) {
            setError(`Failed to delete reviews: ${err.response?.data?.error || err.message}`);
        } finally {
            setBulkDeleting(false);
        }
    };

    const confirmCancelSchedule = async () => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/godreviews/${selectedReview._id}/schedule-telegram`,
                { headers: getAuthHeaders() }
            );
            setShowCancelScheduleConfirm(false);
            setSelectedReview(null);
            refresh();
        } catch (err) {
            setError(`Failed to cancel scheduled send: ${err.response?.data?.error || err.message}`);
        }
    };

    const formatDate = (dateString) => formatSingaporeDateTime(dateString);
    const formatDateDmy = (dateString) => formatSingaporeDateDmy(dateString);
    const publishDateClass = (dateString) =>
        isFutureSingaporeDateTime(dateString) ? 'text-red-600 font-medium' : 'text-gray-500';

    const formatLastPostDisplay = (review) => {
        if (review.pendingScheduledSend?.scheduledAt) {
            return `⏰ ${formatDate(review.pendingScheduledSend.scheduledAt)}`;
        }
        return formatDate(review.lastPostDate);
    };

    const renewalDateFor = (review) => {
        if (review.reviewForModel !== 'godMember') return '—';
        return formatDateDmy(review.reviewFor?.renewalDate);
    };

    const truncate = (str, len = 50) => {
        if (!str) return '—';
        const stripped = str.replace(/<[^>]*>/g, '').trim();
        if (stripped.length <= len) return stripped;
        return `${stripped.slice(0, len)}…`;
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">SLG Reviews</h1>
                        <p className="text-gray-600">Total: {totalReviews} reviews</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {selectMode ? (
                            <>
                                <button
                                    type="button"
                                    onClick={exitSelectMode}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={selectedIds.length === 0}
                                    onClick={() => setShowBulkDeleteConfirm(true)}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300 flex items-center"
                                >
                                    <FiTrash2 className="mr-2" />
                                    Delete ({selectedIds.length})
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setSelectMode(true)}
                                className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 flex items-center"
                            >
                                <FiCheckSquare className="mr-2" />
                                Select
                            </button>
                        )}
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                        >
                            <FiPlus className="mr-2" />
                            Create Review
                        </button>
                    </div>
                </div>

                {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">{error}</div>}

                <div className="mb-3 flex flex-wrap gap-2 items-center">
                    <div className="relative flex-1 min-w-[180px] max-w-[320px]">
                        <input
                            type="text"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 pr-9"
                            placeholder="Search subject (godID, name, webID)..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (setRecommendFilter(false), setSearchTerm(searchInput.trim()), setCurrentPage(1))}
                        />
                        <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                    </div>
                    <button
                        type="button"
                        onClick={() => { setRecommendFilter(false); setSearchTerm(searchInput.trim()); setCurrentPage(1); }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center shrink-0"
                    >
                        <FiSearch className="mr-2" />
                        Search
                    </button>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center shrink-0"
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={handleRecommend}
                        className={`px-4 py-2 rounded-md text-white flex items-center shrink-0 ${recommendFilter ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    >
                        Recommend
                    </button>
                    <select
                        value={forModelFilter}
                        onChange={(e) => { setRecommendFilter(false); setForModelFilter(e.target.value); setCurrentPage(1); }}
                        className="border border-gray-300 rounded-md px-3 py-2"
                    >
                        <option value="">All types</option>
                        <option value="godMember">God Member</option>
                        <option value="NonMember">Non-Member</option>
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => { setRecommendFilter(false); setStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="border border-gray-300 rounded-md px-3 py-2"
                        title="God member status only"
                    >
                        <option value="">Member status: All</option>
                        <option value="active">Active members</option>
                        <option value="cancel">Cancelled members</option>
                    </select>
                    <select
                        value={ratingFilter}
                        onChange={(e) => { setRecommendFilter(false); setRatingFilter(e.target.value); setCurrentPage(1); }}
                        className="border border-gray-300 rounded-md px-3 py-2"
                    >
                        <option value="">All ratings</option>
                        {[5, 4, 3, 2, 1].map((n) => (
                            <option key={n} value={n}>{n} stars</option>
                        ))}
                    </select>
                    <select
                        value={hiddenFilter}
                        onChange={(e) => { setRecommendFilter(false); setHiddenFilter(e.target.value); setCurrentPage(1); }}
                        className="border border-gray-300 rounded-md px-3 py-2"
                    >
                        <option value="">All visibility</option>
                        <option value="false">Visible only</option>
                        <option value="true">Hidden only</option>
                    </select>
                    <select
                        value={lastPostFilter}
                        onChange={(e) => { setRecommendFilter(false); setLastPostFilter(e.target.value); setCurrentPage(1); }}
                        className="border border-gray-300 rounded-md px-3 py-2"
                    >
                        <option value="">Last post: All</option>
                        <option value="scheduled">Last post: Scheduled</option>
                    </select>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Loading...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {selectMode && (
                                            <th className="px-3 py-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={allPageSelected}
                                                    onChange={toggleSelectPage}
                                                    aria-label="Select all on this page"
                                                />
                                            </th>
                                        )}
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">For</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Last review (SG)</th>
                                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">St</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Renewal (SG)</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reviewer</th>
                                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">★</th>
                                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Hidden</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">View</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Published (SG)</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Last post (SG)</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {reviews.length === 0 ? (
                                        <tr>
                                            <td colSpan={selectMode ? 13 : 12} className="px-4 py-6 text-center text-gray-500">No reviews found.</td>
                                        </tr>
                                    ) : (
                                        reviews.map((review) => (
                                            <tr key={review._id} className="hover:bg-gray-50">
                                                {selectMode && (
                                                    <td className="px-3 py-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(review._id)}
                                                            onChange={() => toggleSelectId(review._id)}
                                                            aria-label={`Select review ${review._id}`}
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-3 py-2 text-sm text-blue-700 max-w-[200px] truncate" title={formatReviewSubject(review)}>
                                                    {formatReviewSubject(review)}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{formatDateDmy(review.lastReviewDate)}</td>
                                                <td className="px-3 py-2 text-sm text-center">{memberStatusIcon(review)}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{renewalDateFor(review)}</td>
                                                <td className="px-3 py-2 text-sm">{formatReviewer(review)}</td>
                                                <td className="px-3 py-2 text-sm text-center font-medium">{review.rating}</td>
                                                <td className="px-3 py-2 text-sm text-center">{review.hidden ? 'Yes' : 'No'}</td>
                                                <td className="px-3 py-2 text-sm max-w-xs truncate" title={truncate(review.reviewComment, 200)}>
                                                    {truncate(review.reviewComment)}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-right whitespace-nowrap font-mono">{formatViewObjectCompact(review)}</td>
                                                <td className={`px-3 py-2 text-sm whitespace-nowrap ${publishDateClass(review.publishDate)}`}>{formatDate(review.publishDate)}</td>
                                                <td className={`px-3 py-2 text-sm whitespace-nowrap ${review.pendingScheduledSend ? 'text-amber-700' : 'text-gray-500'}`}>
                                                    {formatLastPostDisplay(review)}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-right whitespace-nowrap">
                                                    <button onClick={() => openView(review)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View"><FiEye size={16} /></button>
                                                    <button onClick={() => openEdit(review)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded ml-1" title="Edit"><FiEdit2 size={16} /></button>
                                                    {review.reviewForModel === 'godMember' && (
                                                        <button onClick={() => openSend(review)} className="p-1.5 text-sky-600 hover:bg-sky-50 rounded ml-1" title="Send to Telegram"><FaTelegramPlane size={16} /></button>
                                                    )}
                                                    {review.pendingScheduledSend && (
                                                        <button
                                                            onClick={() => { setSelectedReview(review); setShowCancelScheduleConfirm(true); }}
                                                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded ml-1"
                                                            title="Cancel scheduled send"
                                                        >
                                                            <FiClock size={16} />
                                                        </button>
                                                    )}
                                                    <button onClick={() => { setSelectedReview(review); setShowDeleteConfirm(true); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded ml-1" title="Delete"><FiTrash2 size={16} /></button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {totalPages > 1 && (
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                )}
            </div>

            {showViewModal && selectedReview && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h2 className="text-lg font-semibold">Review Details</h2>
                            <button onClick={() => { setShowViewModal(false); setSelectedReview(null); }} className="p-2 hover:bg-gray-100 rounded"><FiX size={20} /></button>
                        </div>
                        <div className="p-4 overflow-y-auto flex-1 text-sm space-y-2">
                            <p><span className="font-medium text-gray-600">For:</span> <span className="text-blue-700">{formatReviewSubject(selectedReview)}</span> ({selectedReview.reviewForModel})</p>
                            <p><span className="font-medium text-gray-600">Reviewer:</span> {formatReviewer(selectedReview)}</p>
                            <p><span className="font-medium text-gray-600">Rating:</span> {selectedReview.rating} / 5</p>
                            <p><span className="font-medium text-gray-600">Hidden:</span> {selectedReview.hidden ? 'Yes' : 'No'}</p>
                            <p><span className="font-medium text-gray-600">Views:</span> {formatViewObjectCompact(selectedReview)}</p>
                            <p>
                                <span className="font-medium text-gray-600">Published (SG):</span>{' '}
                                <span className={publishDateClass(selectedReview.publishDate)}>
                                    {formatDate(selectedReview.publishDate)}
                                </span>
                            </p>
                            <p>
                                <span className="font-medium text-gray-600">Last post (SG):</span>{' '}
                                <span className={selectedReview.pendingScheduledSend ? 'text-amber-700' : ''}>
                                    {formatLastPostDisplay(selectedReview)}
                                </span>
                            </p>
                            {selectedReview.pendingScheduledSend && (
                                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                    <p className="text-sm text-amber-900 flex-1">
                                        Telegram send scheduled for {formatDate(selectedReview.pendingScheduledSend.scheduledAt)} (SG)
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { setShowViewModal(false); setShowCancelScheduleConfirm(true); }}
                                        className="px-3 py-1.5 text-sm border border-amber-500 text-amber-700 rounded-md hover:bg-amber-100"
                                    >
                                        Cancel schedule
                                    </button>
                                </div>
                            )}
                            <div>
                                <span className="font-medium text-gray-600 block mb-1">Comment:</span>
                                <div
                                    className="text-gray-800 border border-gray-200 rounded p-3 bg-gray-50 prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: selectedReview.reviewComment || '—' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCreateModal && (
                <GodReviewModal onClose={() => setShowCreateModal(false)} onSuccess={() => { setShowCreateModal(false); refresh(); }} getAuthHeaders={getAuthHeaders} />
            )}
            {showEditModal && selectedReview && (
                <GodReviewModal
                    initialReview={selectedReview}
                    onClose={() => { setShowEditModal(false); setSelectedReview(null); }}
                    onSuccess={() => { setShowEditModal(false); setSelectedReview(null); refresh(); }}
                    getAuthHeaders={getAuthHeaders}
                />
            )}
            {showDeleteConfirm && selectedReview && (
                <DeleteConfirmationModal
                    title="Delete Review"
                    message={`Delete review by "${formatReviewer(selectedReview)}" for ${formatReviewSubject(selectedReview)}?`}
                    onConfirm={confirmDelete}
                    onCancel={() => { setShowDeleteConfirm(false); setSelectedReview(null); }}
                />
            )}
            {showBulkDeleteConfirm && (
                <DeleteConfirmationModal
                    title={`Delete ${selectedIds.length} Review?`}
                    message={`Delete ${selectedIds.length} Review?`}
                    isLoading={bulkDeleting}
                    onConfirm={confirmBulkDelete}
                    onCancel={() => { if (!bulkDeleting) setShowBulkDeleteConfirm(false); }}
                />
            )}
            {showCancelScheduleConfirm && selectedReview && (
                <DeleteConfirmationModal
                    title="Cancel scheduled send"
                    message={`Cancel Telegram send scheduled for ${formatDate(selectedReview.pendingScheduledSend?.scheduledAt)} (SG)?`}
                    onConfirm={confirmCancelSchedule}
                    onCancel={() => { setShowCancelScheduleConfirm(false); setSelectedReview(null); }}
                />
            )}
            {showSendModal && selectedReview && (
                <ReviewSendTelegramModal
                    reviewId={selectedReview._id}
                    onClose={() => { setShowSendModal(false); setSelectedReview(null); }}
                    onSuccess={refresh}
                    getAuthHeaders={getAuthHeaders}
                />
            )}
        </Layout>
    );
};

export default GodReviewPage;
